import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Save, ArrowUp, ArrowDown } from 'lucide-react';
import { api, type ApiError } from '../../api';
import type { Rubric } from '../../../shared/rubrics';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { cn } from '../../lib/utils';

type Row = { id: string; label: string; description: string; maxScore: string; weight: string };
const toRow = (c: Rubric['criteria'][number]): Row => ({
  id: c.id,
  label: c.label,
  description: c.description ?? '',
  maxScore: String(c.maxScore ?? 5),
  weight: c.weight == null ? '' : String(c.weight),
});

type Data = { rubrics: Rubric[]; categories: string[] };

export default function ManageCriteria({ yearId }: { yearId: number }) {
  const [data, setData] = useState<Data>({ rubrics: [], categories: [] });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [cats, setCats] = useState<string[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  function applySelection(d: Data, id: string | null) {
    setSelectedId(id);
    const r = d.rubrics.find((x) => x.id === id) ?? null;
    setName(r?.name ?? '');
    setCats(r?.categories ?? []);
    setRows((r?.criteria ?? []).map(toRow));
  }

  async function load(selectId?: string | null) {
    setLoading(true);
    try {
      const d = await api<Data>(`admin-rubrics?yearId=${yearId}`);
      setData(d);
      applySelection(d, selectId !== undefined ? selectId : (d.rubrics[0]?.id ?? null));
    } catch (e) {
      toast.error((e as ApiError).message || 'Failed to load rubrics');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearId]);

  const selected = data.rubrics.find((r) => r.id === selectedId) ?? null;
  const update = (i: number, patch: Partial<Row>) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const move = (i: number, dir: -1 | 1) =>
    setRows((rs) => {
      const j = i + dir;
      if (j < 0 || j >= rs.length) return rs;
      const next = [...rs];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  const toggleCat = (c: string) => setCats((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));

  const weightSum = rows.reduce((s, r) => s + (r.weight === '' ? 0 : Number(r.weight) || 0), 0);
  const anyWeighted = rows.some((r) => r.weight !== '');

  async function newRubric() {
    setBusy(true);
    try {
      const d = await api<Data>('admin-rubrics', { body: { action: 'create', yearId, name: `Rubric ${data.rubrics.length + 1}` } });
      setData(d);
      applySelection(d, d.rubrics[d.rubrics.length - 1]?.id ?? null);
      toast.success('Rubric created');
    } catch (e) {
      toast.error((e as ApiError).message || 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!selectedId) return;
    setBusy(true);
    try {
      const d = await api<Data>('admin-rubrics', { body: { action: 'save', rubricId: selectedId, name, categories: cats, criteria: rows } });
      setData(d);
      applySelection(d, selectedId);
      toast.success('Rubric saved');
    } catch (e) {
      toast.error((e as ApiError).message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (!selected) return;
    if (!confirm(`Delete the "${selected.name}" rubric?`)) return;
    setBusy(true);
    try {
      const d = await api<Data>('admin-rubrics', { body: { action: 'delete', rubricId: selected.id } });
      setData(d);
      applySelection(d, d.rubrics[0]?.id ?? null);
      toast.success('Rubric deleted');
    } catch (e) {
      toast.error((e as ApiError).message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  if (loading)
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-28 w-full rounded-lg" />
        <Skeleton className="h-28 w-full rounded-lg" />
      </div>
    );

  return (
    <div className="space-y-4">
      {/* top controls: switch rubric, create, save, delete */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={selectedId ?? ''}
          onChange={(e) => applySelection(data, e.target.value || null)}
          className="h-9 rounded-md border border-input bg-card px-2 text-sm font-medium shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {data.rubrics.length === 0 && <option value="">No rubrics yet</option>}
          {data.rubrics.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
              {r.categories.length === 0 ? ' • all' : ''}
            </option>
          ))}
        </select>
        <Button variant="outline" size="sm" onClick={newRubric} disabled={busy}>
          <Plus /> New rubric
        </Button>
        {selected && (
          <div className="ml-auto flex gap-2">
            <Button size="sm" onClick={save} disabled={busy || rows.length === 0}>
              <Save /> Save rubric
            </Button>
            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={del} disabled={busy}>
              <Trash2 /> Delete
            </Button>
          </div>
        )}
      </div>

      {!selected ? (
        <Card className="p-8 text-center text-muted-foreground">No rubrics yet. Create one to score presentations by category.</Card>
      ) : (
        <>
          <Card className="space-y-4 p-4">
            <div className="space-y-1.5">
              <Label>Rubric name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Research" />
            </div>
            <div className="space-y-1.5">
              <Label>Applies to categories</Label>
              {data.categories.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No categories on this year's presentations yet — this rubric applies to everything.
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-1.5">
                    {data.categories.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => toggleCat(c)}
                        className={cn(
                          'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                          cats.includes(c)
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-input bg-card text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {cats.length === 0
                      ? 'No categories selected → this rubric applies to all presentations.'
                      : 'A category belongs to one rubric — selecting it here moves it off any other.'}
                  </p>
                </>
              )}
            </div>
          </Card>

          {anyWeighted && (
            <p className={cn('text-sm', Math.abs(weightSum - 1) < 0.001 ? 'text-muted-foreground' : 'text-warning-foreground')}>
              Weights total <span className="font-semibold tabular-nums">{Math.round(weightSum * 100) / 100}</span>
              {Math.abs(weightSum - 1) < 0.001 ? ' ✓ (should sum to 1.0)' : ' — weighted criteria should sum to 1.0'}
            </p>
          )}

          <Button variant="outline" size="sm" onClick={() => setRows((rs) => [...rs, { id: '', label: '', description: '', maxScore: '5', weight: '' }])}>
            <Plus /> Add criterion
          </Button>

          <div className="space-y-3">
            {rows.map((r, i) => (
              <Card key={i} className="space-y-3 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 space-y-1.5">
                    <Label>Criterion</Label>
                    <Input value={r.label} onChange={(e) => update(i, { label: e.target.value })} placeholder="Relevance to ADHD Field" />
                  </div>
                  <div className="flex flex-col gap-1 pt-6">
                    <Button size="icon" variant="ghost" className="size-7" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up"><ArrowUp /></Button>
                    <Button size="icon" variant="ghost" className="size-7" onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label="Move down"><ArrowDown /></Button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Description</Label>
                  <Input value={r.description} onChange={(e) => update(i, { description: e.target.value })} placeholder="What this criterion measures" />
                </div>
                <div className="flex items-end gap-3">
                  <div className="w-24 space-y-1.5">
                    <Label>Max score</Label>
                    <Input type="number" min={1} value={r.maxScore} onChange={(e) => update(i, { maxScore: e.target.value })} />
                  </div>
                  <div className="w-28 space-y-1.5">
                    <Label>Weight</Label>
                    <Input type="number" step="0.05" value={r.weight} onChange={(e) => update(i, { weight: e.target.value })} placeholder="—" />
                  </div>
                  <span className="pb-2.5 text-xs text-muted-foreground">Leave weight blank for an unweighted rubric.</span>
                  <Button size="icon" variant="ghost" className="ml-auto text-destructive hover:text-destructive" onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))} aria-label="Remove"><Trash2 /></Button>
                </div>
              </Card>
            ))}
            {rows.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No criteria yet — add at least one.</p>}
          </div>
        </>
      )}
    </div>
  );
}
